/**
 * 音楽家向けページ（/musician）用の選択肢とプロンプト
 */

export interface MusicianOption {
  id: string;
  name: string;
  prompt: string;
}

/** 背景 */
export const MUSICIAN_BACKGROUND_OPTIONS: MusicianOption[] = [
  { id: 'studio_white', name: 'スタジオ（ホワイト）', prompt: 'プロフェッショナルな白いスタジオ背景、清潔感のある照明' },
  { id: 'studio_gray', name: 'スタジオ（グレー）', prompt: 'プロフェッショナルなグレーのスタジオ背景、上品な照明' },
  { id: 'studio_dark_gray', name: 'スタジオ（ダークグレー）', prompt: 'プロフェッショナルなダークグレーのスタジオ背景、洗練された照明' },
  { id: 'studio_light_purple', name: 'スタジオ（ライトパープル）', prompt: 'プロフェッショナルなライトパープルのスタジオ背景、洗練された照明' },
  { id: 'studio_light_pink', name: 'スタジオ（ライトピンク）', prompt: 'プロフェッショナルなライトピンクのスタジオ背景、温かみのある照明' },
  { id: 'studio_light_blue', name: 'スタジオ（ライトブルー）', prompt: 'プロフェッショナルなライトブルーのスタジオ背景、清々しい照明' },
  { id: 'custom_text', name: 'その他（テキストで指定）', prompt: '' },
  { id: 'custom_image', name: 'その他（写真を添付する）', prompt: '' },
];

/** 衣装（ドレス） */
export const MUSICIAN_DRESS_OPTIONS: MusicianOption[] = [
  { id: 'dress_black', name: 'ドレス（ブラック）', prompt: '上品な黒のドレス、音楽家らしいフォーマルな装い' },
  { id: 'dress_red', name: 'ドレス（レッド）', prompt: '華やかな赤のドレス、ステージ映えする装い' },
  { id: 'dress_pink', name: 'ドレス（ピンク）', prompt: '柔らかいピンクのドレス、優しい印象' },
  { id: 'dress_blue', name: 'ドレス（ブルー）', prompt: '落ち着いた青のドレス、上品な装い' },
  { id: 'dress_green', name: 'ドレス（グリーン）', prompt: '自然で上品なグリーンのドレス' },
  { id: 'custom_text', name: 'その他（テキストで指定）', prompt: '' },
  { id: 'custom_image', name: 'その他（写真を添付する）', prompt: '' },
];

/** 袖のデザイン */
export const MUSICIAN_SLEEVE_OPTIONS: MusicianOption[] = [
  { id: 'any', name: 'おまかせ', prompt: '' },
  { id: 'sleeveless', name: 'ノースリーブ', prompt: 'ノースリーブ' },
  { id: 'off_shoulder', name: 'オフショルダー', prompt: 'オフショルダーのデザイン' },
  { id: 'half_sleeve', name: '半袖', prompt: '半袖' },
  { id: 'long_sleeve', name: '長袖', prompt: '長袖' },
  { id: 'custom_text', name: 'その他（テキストで指定）', prompt: '' },
];

/** 楽器・アイテムなど */
export const MUSICIAN_ITEM_OPTIONS: MusicianOption[] = [
  { id: 'none', name: 'アイテムなし（ポーズのみ）', prompt: '' },
  { id: 'violin', name: 'バイオリン', prompt: 'バイオリンを持ったポーズ、楽器は自然に構えている' },
  { id: 'microphone', name: 'マイク', prompt: 'マイクを持つ、またはスタンドマイクの前で歌うポーズ' },
  { id: 'sheet_music', name: '楽譜', prompt: '楽譜を持っている、または譜面台の前に立つポーズ' },
  { id: 'baton', name: '指揮棒', prompt: '指揮棒を持った指揮者のポーズ' },
  { id: 'custom_text', name: 'その他（テキストで指定）', prompt: '' },
];

/** 顔の向き（既存のポーズと対応） */
export const MUSICIAN_POSE_OPTIONS: MusicianOption[] = [
  { id: 'front', name: '正面', prompt: '真っ直ぐに正面を向き、背筋を伸ばし、あごを軽く引き、柔らかな微笑みでカメラ目線。' },
  { id: 'slight_right', name: 'やや右向き', prompt: '背筋を伸ばし、あごを軽く引き、右に少し向きながら柔らかな微笑みでカメラ目線。' },
  { id: 'slight_left', name: 'やや左向き', prompt: '背筋を伸ばし、あごを軽く引き、左に少し向きながら柔らかな微笑みでカメラ目線。' },
  { id: 'right', name: '右向き', prompt: '背筋を伸ばし、あごを軽く引き、右を向いたプロフィールポーズで、上品で魅力的な横顔。' },
  { id: 'left', name: '左向き', prompt: '背筋を伸ばし、あごを軽く引き、左を向いたプロフィールポーズで、上品で魅力的な横顔。' },
  { id: 'lookback', name: '見返り', prompt: '肩越しに振り返るような自然なポーズで、少し後ろを振り返りながらもカメラに視線を向ける。首を軽く回転させ、上品で魅力的な見返りポーズ。' },
];

export const getMusicianBackgroundById = (id: string) =>
  MUSICIAN_BACKGROUND_OPTIONS.find((b) => b.id === id);
export const getMusicianDressById = (id: string) =>
  MUSICIAN_DRESS_OPTIONS.find((d) => d.id === id);
export const getMusicianSleeveById = (id: string) =>
  MUSICIAN_SLEEVE_OPTIONS.find((s) => s.id === id);
export const getMusicianItemById = (id: string) =>
  MUSICIAN_ITEM_OPTIONS.find((i) => i.id === id);
export const getMusicianPoseById = (id: string) =>
  MUSICIAN_POSE_OPTIONS.find((p) => p.id === id);
